import { GetServerSideProps } from 'next';
import { Params } from 'next/dist/server/router';
import Head from 'next/head';
import React, { ReactElement, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { courseTitleBrowser } from '@common/constant/courseConstant';
import { ROUTES } from '@common/constant/routeConstant';
import { isApiResponseSuccess } from '@common/utility/http';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { LearningTypeId } from 'src/types/product';

import Seo from '@components/atoms/seo';
import GuestLayout from '@components/layouts/guestLayout';
import RootLayout from '@components/layouts/rootLayout';
import CourseDetail from '@components/templates/courseDetail';
import { setAffiliateCookie } from '@helpers/cookieUtils';
import { NextPageWithLayout } from '@pages/_app';
import courseActions from '@redux/course/actions';
import courseApi from '@redux/course/api';
import { CourseEntity } from '@redux/course/type/courseDetailType';

type CoursePageProps = {
  course: CourseEntity;
};

const { fetchCourseDetailSeoAction } = courseActions;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, params } = context as Params;

  const response = await courseApi.fetchCourseDetailSeo({ courseIdOrIdName: params.idName });
  if (isApiResponseSuccess(response.status)) {
    const course = response?.data || null;

    if (!course) {
      return {
        notFound: true,
      };
    }

    if (course.productLearningTypeId === LearningTypeId.CPD) {
      if (course.redirectUrl) {
        return {
          redirect: {
            destination: course.redirectUrl,
            permanent: false,
          },
        };
      }

      return {
        redirect: {
          destination: ROUTES.COURSE.CPD(course.idName),
          permanent: false,
        },
      };
    }

    return {
      props: {
        ...(await serverSideTranslations(locale, ['common', 'store_course'])),
        course,
      },
    };
  }
  if (response.status === 'COURSE_NOT_FOUND') {
    return {
      notFound: true,
    };
  }

  throw new Error('unable to get course');
};

const CoursePage: NextPageWithLayout<CoursePageProps> = (props) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (props.course) {
      dispatch(fetchCourseDetailSeoAction.success(props.course));
    }
    setAffiliateCookie();
  }, [props.course]);

  return (
    <>
      <Head>
        {props.course ? <title>{courseTitleBrowser(props.course?.name)}</title> : null}

        {!props.course?.seoEnable ? (
          <>
            <meta key="meta-googlebot" name="googlebot" content="noindex,nofollow" />
            <meta key="meta-google" name="google" content="nositelinkssearchbox" />
          </>
        ) : null}
      </Head>

      <Seo
        title={courseTitleBrowser(props.course.name)}
        description={props.course.fbContent}
        image_url={props.course.shareThumbnailUrl}
      />

      <CourseDetail courseDetailSeo={props.course} />
    </>
  );
};

CoursePage.getLayout = (page: ReactElement) => (
  <RootLayout>
    <GuestLayout>{page}</GuestLayout>
  </RootLayout>
);

export default CoursePage;
